import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Collapse,
  Divider,
  Alert
} from '@mui/material';
import {
  SmartToy as AIIcon,
  Send as SendIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Restaurant as RestaurantIcon,
  TrendingUp as TrendingIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

interface AIResponse {
  id: string;
  question: string;
  answer: string;
  timestamp: Date;
  category: 'sales' | 'inventory' | 'staff' | 'general';
}

const AIAssistant: React.FC = () => {
  const [question, setQuestion] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [responses, setResponses] = useState<AIResponse[]>([
    {
      id: '1',
      question: 'What were our top selling items last week?',
      answer: 'Based on last week\'s data, your top selling items were:\n1. Birria Rice Bowl (127 orders)\n2. Chicken Tacos (98 orders)\n3. Steak Burrito (89 orders)\n\nTotal revenue: $8,456.32 (+12% vs previous week)',
      timestamp: new Date(Date.now() - 3600000),
      category: 'sales'
    },
    {
      id: '2',
      question: 'Do we need to restock any ingredients?',
      answer: 'Yes, you should restock:\n• Ground beef (running low - 15% remaining)\n• Flour tortillas (critical - 8% remaining)\n• Avocados (moderate - 35% remaining)\n\nRecommended order quantities available in your inventory dashboard.',
      timestamp: new Date(Date.now() - 7200000),
      category: 'inventory'
    }
  ]);
  const [expanded, setExpanded] = useState<string[]>(['1']);

  const handleAskQuestion = async () => {
    if (!question.trim()) return;

    setIsLoading(true);
    
    // Simulate AI response
    setTimeout(() => {
      const newResponse: AIResponse = {
        id: Date.now().toString(),
        question: question,
        answer: `This is a simulated AI response to: "${question}". In the real system, this would connect to your AI wrapper and provide intelligent insights about your restaurant operations, sales data, customer preferences, and business recommendations.`,
        timestamp: new Date(),
        category: 'general'
      };
      
      setResponses(prev => [newResponse, ...prev]);
      setQuestion('');
      setIsLoading(false);
    }, 2000);
  };

  const handleToggleExpanded = (id: string) => {
    setExpanded(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'sales': return <TrendingIcon />;
      case 'inventory': return <InventoryIcon />;
      case 'staff': return <PeopleIcon />;
      case 'general': return <RestaurantIcon />;
      default: return <RestaurantIcon />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'sales': return 'success';
      case 'inventory': return 'warning';
      case 'staff': return 'info';
      case 'general': return 'default';
      default: return 'default';
    }
  };

  return (
    <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <AIIcon sx={{ fontSize: 32, color: '#424242', mr: 2 }} />
        <Box>
          <Typography variant="h5" component="h2" gutterBottom>
            AI Business Assistant
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ask questions about your restaurant operations, sales, inventory, and more
          </Typography>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Revolutionary Feature:</strong> This AI assistant integrates with your POS data, 
          restaurant database, and provides intelligent business insights in real-time.
        </Typography>
      </Alert>

      {/* Question Input */}
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Ask about sales, inventory, staff, or any business question..."
          variant="outlined"
          size="small"
          value={question}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuestion(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') handleAskQuestion();
          }}
          disabled={isLoading}
        />
        <Button
          variant="contained"
          onClick={handleAskQuestion}
          disabled={!question.trim() || isLoading}
          startIcon={<SendIcon />}
        >
          {isLoading ? 'Thinking...' : 'Ask AI'}
        </Button>
      </Box>

      {/* Suggested Questions */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Questions:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            'What were our top sellers today?',
            'Do we need to restock anything?',
            'How is our staff performance?',
            'What are customer preferences?'
          ].map((suggestion) => (
            <Chip
              key={suggestion}
              label={suggestion}
              size="small"
              variant="outlined"
              onClick={() => setQuestion(suggestion)}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* AI Responses */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Typography variant="subtitle2" gutterBottom>
          Recent Conversations:
        </Typography>
        <List sx={{ p: 0 }}>
          {responses.map((response) => (
            <Paper key={response.id} sx={{ mb: 2, overflow: 'hidden' }}>
              <ListItem
                component="div"
                onClick={() => handleToggleExpanded(response.id)}
                sx={{ 
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.08)' },
                  cursor: 'pointer'
                }}
              >
                <ListItemIcon>
                  {getCategoryIcon(response.category)}
                </ListItemIcon>
                <ListItemText
                  primary={response.question}
                  secondary={response.timestamp.toLocaleString()}
                  primaryTypographyProps={{ variant: 'subtitle2', fontWeight: 'medium' }}
                />
                <Chip
                  label={response.category}
                  size="small"
                  color={getCategoryColor(response.category) as any}
                  sx={{ mr: 1 }}
                />
                {expanded.includes(response.id) ? <CollapseIcon /> : <ExpandIcon />}
              </ListItem>
              
              <Collapse in={expanded.includes(response.id)}>
                <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                    {response.answer}
                  </Typography>
                </Box>
              </Collapse>
            </Paper>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

export default AIAssistant;
